import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Receipt, Loader2, CheckCircle2, Clock, AlertTriangle, CreditCard,
  Upload, Image, XCircle, Eye, RefreshCw, Wallet, FileText, Archive
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "@/hooks/use-toast";
import { getSignedFileUrl } from "@/lib/storage-utils";

interface Props {
  childIds: string[];
}

const statusMap: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: any; className?: string }> = {
  paid: { label: "مدفوع", variant: "default", icon: CheckCircle2 },
  pending: { label: "معلق", variant: "secondary", icon: Clock },
  overdue: { label: "متأخر", variant: "destructive", icon: AlertTriangle },
  awaiting_confirmation: { label: "بانتظار التأكيد", variant: "outline", icon: Clock, className: "border-orange-300 text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400" },
  under_review: { label: "قيد المراجعة", variant: "outline", icon: Eye, className: "border-blue-300 text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400" },
  rejected: { label: "مرفوض", variant: "destructive", icon: XCircle },
};

const feeTypeMap: Record<string, string> = {
  monthly: "شهري",
  registration: "تسجيل",
  transportation: "نقل",
  activities: "أنشطة",
  other: "أخرى",
};

const paymentMethodMap: Record<string, string> = {
  cash: "كاش",
  bank_transfer: "تحويل بنكي",
};

export default function ParentInvoices({ childIds }: Props) {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [payingInvoice, setPayingInvoice] = useState<any | null>(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [receiptViewUrl, setReceiptViewUrl] = useState<string | null>(null);
  const [viewingReceipt, setViewingReceipt] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (childIds.length > 0) fetchInvoices();
    else setLoading(false);
  }, [childIds]);

  const fetchInvoices = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("invoices")
      .select("*, children(full_name, class_id, classes(name))")
      .in("child_id", childIds)
      .order("created_at", { ascending: false })
      .limit(100);
    setInvoices(data || []);
    setLoading(false);
  };

  const pendingInvoices = invoices.filter(i => ["pending", "overdue", "rejected"].includes(i.status));
  const reviewInvoices = invoices.filter(i => ["awaiting_confirmation", "under_review"].includes(i.status));
  const paidInvoices = invoices.filter(i => i.status === "paid");

  const totalPending = pendingInvoices.reduce((sum, i) => sum + (i.amount || 0), 0);
  const totalPaid = paidInvoices.reduce((sum, i) => sum + (i.amount || 0), 0);

  const handleConfirmPayment = async () => {
    if (!payingInvoice) return;
    setSubmitting(true);

    if (paymentMethod === "cash") {
      const { error } = await supabase
        .from("invoices")
        .update({ status: "awaiting_confirmation", payment_method: "cash" })
        .eq("id", payingInvoice.id);

      if (error) {
        toast({ title: "خطأ", description: "فشل إرسال طلب الدفع", variant: "destructive" });
      } else {
        toast({ title: "تم الإرسال", description: "تم إرسال طلب الدفع. انتظر تأكيد الحضانة." });
        await fetchInvoices();
      }
    } else {
      if (!receiptFile) {
        toast({ title: "مطلوب", description: "يرجى رفع صورة إيصال التحويل", variant: "destructive" });
        setSubmitting(false);
        return;
      }

      setUploadingFile(true);
      const fileExt = receiptFile.name.split(".").pop();
      const filePath = `${payingInvoice.child_id}/${payingInvoice.id}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("payment-receipts")
        .upload(filePath, receiptFile, { upsert: true });

      if (uploadError) {
        toast({ title: "خطأ", description: "فشل رفع الإيصال: " + uploadError.message, variant: "destructive" });
        setUploadingFile(false);
        setSubmitting(false);
        return;
      }

      const { error } = await supabase
        .from("invoices")
        .update({ status: "under_review", payment_method: "bank_transfer", receipt_url: filePath })
        .eq("id", payingInvoice.id);

      setUploadingFile(false);

      if (error) {
        toast({ title: "خطأ", description: "فشل تحديث الفاتورة", variant: "destructive" });
      } else {
        toast({ title: "تم الإرسال", description: "تم رفع الإيصال وإرساله للمراجعة." });
        await fetchInvoices();
      }
    }

    setSubmitting(false);
    setPayingInvoice(null);
    setPaymentMethod("cash");
    setReceiptFile(null);
    setPreviewUrl(null);
  };

  const handleReupload = (inv: any) => {
    setPayingInvoice(inv);
    setPaymentMethod("bank_transfer");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "خطأ", description: "حجم الملف كبير جداً (الحد الأقصى 10MB)", variant: "destructive" });
      return;
    }
    setReceiptFile(file);
    setPreviewUrl(file.type.startsWith("image/") ? URL.createObjectURL(file) : null);
  };

  const handleViewReceipt = async (receiptUrl: string) => {
    const url = await getSignedFileUrl("payment-receipts", receiptUrl);
    setReceiptViewUrl(url);
    setViewingReceipt(true);
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (invoices.length === 0) {
    return (
      <Card className="shadow-card">
        <CardContent className="py-12 text-center">
          <Receipt className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">لا توجد فواتير بعد</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={AlertTriangle} iconClass="text-amber-500" value={pendingInvoices.length} label="مستحقة" valueClass="text-amber-500" />
        <StatCard icon={Eye} iconClass="text-blue-500" value={reviewInvoices.length} label="قيد المراجعة" valueClass="text-blue-500" />
        <StatCard icon={CheckCircle2} iconClass="text-primary" value={paidInvoices.length} label="مدفوعة" valueClass="text-primary" />
        <StatCard icon={Wallet} iconClass="text-primary" value={`${totalPaid.toLocaleString()} ج.م`} label="إجمالي المدفوع" valueClass="text-primary" />
      </div>

      {/* Pending Amount Alert */}
      {totalPending > 0 && (
        <Card className="shadow-card border-destructive/20 bg-destructive/5">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">مبلغ مستحق</p>
              <p className="text-lg font-bold text-destructive">{totalPending.toLocaleString()} ج.م</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="pending" dir="rtl">
        <TabsList className="w-full grid grid-cols-3">
          <TabsTrigger value="pending" className="gap-1.5 text-xs sm:text-sm">
            <CreditCard className="w-4 h-4" />
            مستحقة
            {pendingInvoices.length > 0 && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4 mr-1">{pendingInvoices.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="review" className="gap-1.5 text-xs sm:text-sm">
            <Eye className="w-4 h-4" />
            قيد المراجعة
            {reviewInvoices.length > 0 && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 mr-1">{reviewInvoices.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="archive" className="gap-1.5 text-xs sm:text-sm">
            <Archive className="w-4 h-4" />
            أرشيف المدفوعات
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {pendingInvoices.length === 0 ? (
            <EmptyState icon={CheckCircle2} message="لا توجد فواتير مستحقة 🎉" />
          ) : (
            <InvoiceList invoices={pendingInvoices} onPay={setPayingInvoice} onReupload={handleReupload} />
          )}
        </TabsContent>

        <TabsContent value="review">
          {reviewInvoices.length === 0 ? (
            <EmptyState icon={Clock} message="لا توجد فواتير قيد المراجعة" />
          ) : (
            <InvoiceList invoices={reviewInvoices} />
          )}
        </TabsContent>

        <TabsContent value="archive">
          {paidInvoices.length === 0 ? (
            <EmptyState icon={Archive} message="لا توجد مدفوعات سابقة" />
          ) : (
            <InvoiceList invoices={paidInvoices} showPaidDetails onViewReceipt={handleViewReceipt} />
          )}
        </TabsContent>
      </Tabs>

      {/* Payment Confirmation Dialog */}
      <Dialog open={!!payingInvoice} onOpenChange={(open) => {
        if (!open) {
          setPayingInvoice(null);
          setReceiptFile(null);
          setPreviewUrl(null);
          setPaymentMethod("cash");
        }
      }}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>تأكيد دفع الفاتورة</DialogTitle>
            <DialogDescription>
              {payingInvoice?.title} — {payingInvoice?.amount?.toLocaleString()} ج.م
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Label className="text-sm font-medium">طريقة الدفع</Label>
            <RadioGroup value={paymentMethod} onValueChange={(v) => { setPaymentMethod(v); setReceiptFile(null); setPreviewUrl(null); }} className="gap-3">
              <div className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50">
                <RadioGroupItem value="cash" id="cash" />
                <Label htmlFor="cash" className="cursor-pointer flex-1">
                  <span>كاش</span>
                  <p className="text-[10px] text-muted-foreground mt-0.5">سيتم إرسال الطلب وانتظار تأكيد الحضانة</p>
                </Label>
              </div>
              <div className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50">
                <RadioGroupItem value="bank_transfer" id="bank_transfer" />
                <Label htmlFor="bank_transfer" className="cursor-pointer flex-1">
                  <span>تحويل بنكي</span>
                  <p className="text-[10px] text-muted-foreground mt-0.5">يجب رفع صورة إيصال التحويل</p>
                </Label>
              </div>
            </RadioGroup>

            {paymentMethod === "bank_transfer" && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">صورة إيصال التحويل</Label>
                <input ref={fileInputRef} type="file" accept="image/*,.pdf" className="hidden" onChange={handleFileChange} />
                {!receiptFile ? (
                  <div
                    className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">اضغط لرفع صورة الإيصال</p>
                    <p className="text-[10px] text-muted-foreground/60 mt-1">JPEG, PNG أو PDF — حد أقصى 10MB</p>
                  </div>
                ) : (
                  <div className="border rounded-lg p-3 space-y-2">
                    {previewUrl && <img src={previewUrl} alt="إيصال" className="w-full max-h-48 object-contain rounded" />}
                    <div className="flex items-center justify-between">
                      <span className="text-sm truncate">{receiptFile.name}</span>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => { setReceiptFile(null); setPreviewUrl(null); }}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-row-reverse gap-2">
            <Button onClick={handleConfirmPayment} disabled={submitting || uploadingFile}>
              {submitting || uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : paymentMethod === "cash" ? "إرسال طلب الدفع" : "رفع الإيصال وإرسال"}
            </Button>
            <Button variant="outline" onClick={() => { setPayingInvoice(null); setReceiptFile(null); setPreviewUrl(null); }} disabled={submitting}>
              إلغاء
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Viewer Dialog */}
      <Dialog open={viewingReceipt} onOpenChange={setViewingReceipt}>
        <DialogContent dir="rtl" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>عرض الإيصال</DialogTitle>
          </DialogHeader>
          {receiptViewUrl && (
            receiptViewUrl.includes(".pdf") ? (
              <iframe src={receiptViewUrl} className="w-full h-96 rounded border" />
            ) : (
              <img src={receiptViewUrl} alt="إيصال الدفع" className="w-full max-h-[500px] object-contain rounded" />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Sub-components ─── */

function StatCard({ icon: Icon, iconClass, value, label, valueClass }: { icon: any; iconClass: string; value: string | number; label: string; valueClass: string }) {
  return (
    <Card className="shadow-card">
      <CardContent className="p-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <Icon className={`w-4 h-4 ${iconClass}`} />
        </div>
        <div>
          <p className={`text-lg font-bold leading-tight ${valueClass}`}>{value}</p>
          <p className="text-[11px] text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ icon: Icon, message }: { icon: any; message: string }) {
  return (
    <Card className="shadow-card">
      <CardContent className="py-10 text-center">
        <Icon className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
        <p className="text-muted-foreground text-sm">{message}</p>
      </CardContent>
    </Card>
  );
}

function InvoiceList({ invoices, onPay, onReupload, showPaidDetails, onViewReceipt }: {
  invoices: any[];
  onPay?: (inv: any) => void;
  onReupload?: (inv: any) => void;
  showPaidDetails?: boolean;
  onViewReceipt?: (url: string) => void;
}) {
  const getStatusStyle = (status: string) => {
    switch (status) {
      case "paid": return "bg-primary/10 text-primary";
      case "overdue": return "bg-destructive/10 text-destructive";
      case "awaiting_confirmation": return "bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400";
      case "under_review": return "bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400";
      case "rejected": return "bg-destructive/10 text-destructive";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <Card className="shadow-card">
      <CardContent className="p-0">
        <ScrollArea className="max-h-[450px]">
          <div className="divide-y">
            {invoices.map((inv) => {
              const st = statusMap[inv.status] || statusMap.pending;
              const StatusIcon = st.icon;
              const canPay = inv.status === "pending" || inv.status === "overdue";
              const isRejected = inv.status === "rejected";
              const className_ = inv.children?.classes?.name;

              return (
                <div key={inv.id} className="p-4 flex items-center gap-3 hover:bg-muted/30 transition-colors">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${getStatusStyle(inv.status)}`}>
                    <StatusIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground text-sm truncate">{inv.title}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap">
                      <span>{inv.children?.full_name}</span>
                      {className_ && (
                        <>
                          <span>•</span>
                          <span>{className_}</span>
                        </>
                      )}
                      <span>•</span>
                      <span>{feeTypeMap[inv.fee_type] || inv.fee_type}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                      استحقاق: {format(new Date(inv.due_date), "dd MMM yyyy", { locale: ar })}
                    </p>
                    {isRejected && inv.rejection_reason && (
                      <p className="text-[11px] text-destructive mt-1 bg-destructive/5 rounded px-2 py-1">
                        سبب الرفض: {inv.rejection_reason}
                      </p>
                    )}
                    {showPaidDetails && (
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground/70 mt-1 flex-wrap">
                        {inv.paid_at && <span>تم الدفع: {format(new Date(inv.paid_at), "dd MMM yyyy", { locale: ar })}</span>}
                        {inv.payment_method && <><span>•</span><span>{paymentMethodMap[inv.payment_method] || inv.payment_method}</span></>}
                        {inv.receipt_url && onViewReceipt && (
                          <>
                            <span>•</span>
                            <button onClick={() => onViewReceipt(inv.receipt_url)} className="text-primary hover:underline flex items-center gap-0.5">
                              <FileText className="w-3 h-3" /> عرض الإيصال
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    {!showPaidDetails && inv.receipt_url && (
                      <div className="flex items-center gap-1 mt-1">
                        <Image className="w-3 h-3 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground">إيصال مرفق</span>
                      </div>
                    )}
                  </div>
                  <div className="text-left shrink-0 space-y-1">
                    <div className="text-base font-bold text-foreground">{inv.amount?.toLocaleString()} ج.م</div>
                    <Badge variant={st.variant} className={`text-[10px] ${st.className || ""}`}>
                      {st.label}
                    </Badge>
                    {canPay && onPay && (
                      <Button size="sm" className="w-full mt-1 text-[10px] h-7" onClick={() => onPay(inv)}>
                        <CreditCard className="w-3 h-3 ml-1" />
                        تأكيد الدفع
                      </Button>
                    )}
                    {isRejected && onReupload && (
                      <Button size="sm" variant="outline" className="w-full mt-1 text-[10px] h-7 border-orange-300 text-orange-600 hover:bg-orange-50" onClick={() => onReupload(inv)}>
                        <RefreshCw className="w-3 h-3 ml-1" />
                        إعادة الرفع
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
